/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a storage, store or state code; it renders nothing */
// C5 Offline storage for the web: the device index and one IndexedDB
// database per owner, every owner write fenced at the storage level (C3).
//
//   cb-offline-device          store `index`, key `index`: DeviceIndex
//                              (lifecycle only: who owns the device data, the
//                              per-owner incarnations, tombstones, the live
//                              transition record, terminal_rejection)
//   cb-offline-<owner_dir>     stores fence, decks, deck_cards, collection,
//                              queue, drafts, meta (owner_dir: acct-<uuid> | guest)
//
// The guarantee (C3 mechanism 4): EVERY owner write is ONE readwrite
// transaction whose scope includes `fence`; its first request reads the fence
// record and the transaction aborts unless the record exists, is open and
// matches the writer's captured owner, incarnation and fence epoch. Writers
// open the database WITHOUT provisioning it: when it does not exist (wiped),
// `onupgradeneeded` fires and the open is aborted, so a resumed writer can
// never recreate a wiped database. Only activateOwnerDb creates it.
//
// localStorage holds NO owner data: only the `cb-offline-fence` mirror (a
// hint every tab reads synchronously; the fence record is the authority).
//
// No browser global is touched at module scope (the prerender imports this
// module through useAuth).
import {
  DEVICE_DB, DEVICE_DB_VERSION, DEVICE_KEY, DEVICE_STORE, FENCE_CLOSED, FENCE_KEY, FENCE_MIRROR_KEY, FENCE_OPEN,
  GUEST_OWNER, LOCK_OWNER_WRITE, OWNER_DB_PREFIX, OWNER_DB_VERSION, OWNER_IMAGE_CACHE_PREFIX, OWNER_STORES, SCHEMA_VERSION, STORE,
  type C3Reason, type OwnerStore,
} from './offline.codes'
import { isAccountOwner, ownerSubject } from './identityMachine'

// ---- Shapes -----------------------------------------------------------------------

export interface Tombstone {
  owner_key: string
  owner_incarnation: number
  reason: C3Reason
  at: string
  /** The owner the device moves to (guest, or the incoming account). */
  next_owner: string
  /** The stored session of that owner could not be verified removed (retried). */
  session_pending?: boolean
  /** Its data could not be wiped (retried). */
  wipe_pending?: boolean
}

export interface TransitionRecord {
  id: string
  reason: C3Reason
  owner_key: string
  fence_epoch: number
  started_at: string
  incoming?: string | null
}

export interface DeviceIndex {
  schema_version: number
  offline_owner: string | null
  owner_incarnations: Record<string, number>
  pending_wipes: Tombstone[]
  transition: TransitionRecord | null
  terminal_rejection: boolean
  /** Owner databases whose delete is blocked (a frozen tab's open connection): retried until gone. */
  pending_db_deletes: string[]
}

export interface FenceRecord {
  owner_key: string
  owner_incarnation: number
  fence_epoch: number
  state: typeof FENCE_OPEN | typeof FENCE_CLOSED
}

export interface FenceMirror {
  owner_key: string | null
  fence_epoch: number
  transition_id: string | null
}

/** What a writer captured when it started (spec 0.3). */
export interface WriterCtx {
  ownerKey: string
  incarnation: number
  fenceEpoch: number
}

export function emptyIndex(): DeviceIndex {
  return { schema_version: SCHEMA_VERSION, offline_owner: null, owner_incarnations: {}, pending_wipes: [], transition: null, terminal_rejection: false, pending_db_deletes: [] }
}

// ---- Names ------------------------------------------------------------------------

/** `acct-<uuid>` or `guest`: no colons in names. */
export function ownerDir(ownerKey: string): string {
  const subject = ownerSubject(ownerKey)
  return subject ? `acct-${subject}` : GUEST_OWNER
}

export function ownerDbName(ownerKey: string): string {
  return `${OWNER_DB_PREFIX}${ownerDir(ownerKey)}`
}

/**
 * The owner's pinned image cache for ONE incarnation: cb-img-<owner_dir>.<n>
 * (W6). Bound to the incarnation so a writer of an older life of the owner
 * (frozen through a wipe) can only ever create, or clean up, a cache no
 * current incarnation uses; it can never touch the current one's.
 */
export function ownerImageCacheName(ownerKey: string, incarnation = 0): string {
  return `${OWNER_IMAGE_CACHE_PREFIX}${ownerDir(ownerKey)}.${incarnation}`
}

/** Any of the owner's image caches, every incarnation (and the bare legacy name). */
export function isOwnerImageCacheOf(name: string, ownerKey: string): boolean {
  const base = `${OWNER_IMAGE_CACHE_PREFIX}${ownerDir(ownerKey)}`
  return name === base || (name.startsWith(`${base}.`) && /^\d+$/.test(name.slice(base.length + 1)))
}

/** The owner key a per-owner database name belongs to, or null (device, shared, unknown). */
export function ownerKeyOfDbName(name: string): string | null {
  if (!name.startsWith(OWNER_DB_PREFIX)) return null
  const dir = name.slice(OWNER_DB_PREFIX.length)
  if (dir === GUEST_OWNER) return GUEST_OWNER
  if (dir.startsWith('acct-') && dir.length > 5) return `acct:${dir.slice(5)}`
  return null
}

export function hasIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined' && indexedDB !== null
}

// ---- IDB helpers ------------------------------------------------------------------

export function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
}

/** Settles on complete or abort; the first request error is kept as the cause (a QuotaExceededError reaches the caller). */
export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    let first: DOMException | null = null
    tx.oncomplete = () => resolve()
    tx.onerror = (ev) => {
      const t = ev.target as IDBRequest | null
      if (!first && t && 'error' in t) first = t.error
    }
    tx.onabort = () => reject(tx.error ?? first ?? new DOMException('Transaction aborted', 'AbortError'))
  })
}

export function isQuotaError(e: unknown): boolean {
  const name = (e as { name?: string } | null)?.name
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED'
}

// ---- Device index -------------------------------------------------------------------

let deviceDb: Promise<IDBDatabase> | null = null

function openDeviceDb(): Promise<IDBDatabase> {
  if (deviceDb) return deviceDb
  const p = new Promise<IDBDatabase>((resolve, reject) => {
    const r = indexedDB.open(DEVICE_DB, DEVICE_DB_VERSION)
    r.onupgradeneeded = () => {
      const db = r.result
      if (!db.objectStoreNames.contains(DEVICE_STORE)) db.createObjectStore(DEVICE_STORE)
    }
    r.onsuccess = () => {
      const db = r.result
      db.onversionchange = () => { db.close(); deviceDb = null }
      resolve(db)
    }
    r.onerror = () => reject(r.error)
  })
  deviceDb = p
  p.catch(() => { if (deviceDb === p) deviceDb = null })
  return p
}

function normalizeIndex(raw: unknown): DeviceIndex {
  const base = emptyIndex()
  if (!raw || typeof raw !== 'object') return base
  const o = raw as Partial<DeviceIndex>
  return {
    schema_version: SCHEMA_VERSION,
    offline_owner: typeof o.offline_owner === 'string' ? o.offline_owner : null,
    owner_incarnations: o.owner_incarnations && typeof o.owner_incarnations === 'object' ? { ...o.owner_incarnations } : {},
    pending_wipes: Array.isArray(o.pending_wipes) ? o.pending_wipes.filter(t => t && typeof t.owner_key === 'string') : [],
    transition: o.transition && typeof o.transition === 'object' && typeof o.transition.owner_key === 'string' ? o.transition : null,
    terminal_rejection: o.terminal_rejection === true,
    pending_db_deletes: Array.isArray(o.pending_db_deletes) ? o.pending_db_deletes.filter((n): n is string => typeof n === 'string') : [],
  }
}

export async function readDeviceIndex(): Promise<DeviceIndex> {
  if (!hasIndexedDb()) return emptyIndex()
  const db = await openDeviceDb()
  const tx = db.transaction(DEVICE_STORE, 'readonly')
  const raw = await req(tx.objectStore(DEVICE_STORE).get(DEVICE_KEY))
  return normalizeIndex(raw)
}

/** One read-modify-write of the device index (a single readwrite transaction, durable on resolve). */
export async function updateDeviceIndex(mutate: (idx: DeviceIndex) => DeviceIndex | void): Promise<DeviceIndex> {
  const db = await openDeviceDb()
  const tx = db.transaction(DEVICE_STORE, 'readwrite')
  const store = tx.objectStore(DEVICE_STORE)
  const done = txDone(tx)
  let next: DeviceIndex = emptyIndex()
  const g = store.get(DEVICE_KEY)
  g.onsuccess = () => {
    const cur = normalizeIndex(g.result)
    next = mutate(cur) ?? cur
    store.put(next, DEVICE_KEY)
  }
  await done
  return next
}

export function incarnationOf(idx: DeviceIndex, ownerKey: string): number {
  const n = idx.owner_incarnations[ownerKey]
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

// ---- The fence mirror (localStorage, a hint) -----------------------------------------

export function readFenceMirror(): FenceMirror | null {
  try {
    const raw = localStorage.getItem(FENCE_MIRROR_KEY)
    if (!raw) return null
    const m = JSON.parse(raw) as Partial<FenceMirror>
    if (typeof m.fence_epoch !== 'number') return null
    return {
      owner_key: typeof m.owner_key === 'string' ? m.owner_key : null,
      fence_epoch: m.fence_epoch,
      transition_id: typeof m.transition_id === 'string' ? m.transition_id : null,
    }
  } catch {
    return null
  }
}

export function writeFenceMirror(m: FenceMirror): void {
  try {
    localStorage.setItem(FENCE_MIRROR_KEY, JSON.stringify(m))
  } catch {
    // No storage: the fence record in IndexedDB is the authority anyway.
  }
}

/**
 * The write gate's fast pre-check (C3 mechanism 2): true only when no
 * transition is in progress and the mirror names the captured owner and
 * fence epoch. A hint: the authoritative check is the fence read inside the
 * write transaction.
 */
export function mayWrite(ctx: WriterCtx): boolean {
  const m = readFenceMirror()
  if (!m) return false
  return m.transition_id === null && m.owner_key === ctx.ownerKey && m.fence_epoch === ctx.fenceEpoch
}

// ---- Owner databases ---------------------------------------------------------------------

const ownerConns = new Map<string, Promise<IDBDatabase | null>>()

function createOwnerStores(db: IDBDatabase): void {
  for (const s of OWNER_STORES) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s)
}

/** Drop this tab's cached connection (before a delete, or after one). */
export function closeOwnerConnection(ownerKey: string): void {
  const name = ownerDbName(ownerKey)
  const p = ownerConns.get(name)
  ownerConns.delete(name)
  void p?.then(db => db?.close(), () => {})
}

/**
 * Open an owner's database WITHOUT provisioning it: null when it does not
 * exist (the upgrade is aborted, so the open cannot recreate a wiped
 * database) or cannot be opened.
 */
export function openOwnerDb(ownerKey: string): Promise<IDBDatabase | null> {
  if (!hasIndexedDb()) return Promise.resolve(null)
  const name = ownerDbName(ownerKey)
  const cached = ownerConns.get(name)
  if (cached) return cached
  const p = new Promise<IDBDatabase | null>((resolve) => {
    let r: IDBOpenDBRequest
    try {
      r = indexedDB.open(name, OWNER_DB_VERSION)
    } catch {
      resolve(null)
      return
    }
    r.onupgradeneeded = (ev) => {
      if (ev.oldVersion === 0) {
        // It does not exist (wiped, or never activated): never create it here.
        try { r.transaction?.abort() } catch { /* already aborting */ }
        return
      }
      createOwnerStores(r.result)
    }
    r.onsuccess = () => {
      const db = r.result
      db.onversionchange = () => {
        db.close()
        if (ownerConns.get(name) === p) ownerConns.delete(name)
      }
      db.onclose = () => { if (ownerConns.get(name) === p) ownerConns.delete(name) }
      resolve(db)
    }
    r.onerror = () => resolve(null)
    r.onblocked = () => { /* waits for the other connection to close */ }
  })
  ownerConns.set(name, p)
  void p.then(db => { if (!db && ownerConns.get(name) === p) ownerConns.delete(name) })
  return p
}

/**
 * Owner activation (sign-in, after C2 set offline_owner, or a cancelled
 * transition reopening the fence): create the database if needed and write
 * an OPEN fence record for (owner, incarnation, epoch). The only path that
 * provisions a database.
 */
export async function activateOwnerDb(ownerKey: string, incarnation: number, fenceEpoch: number): Promise<void> {
  const name = ownerDbName(ownerKey)
  closeOwnerConnection(ownerKey)
  const db = await new Promise<IDBDatabase>((resolve, reject) => {
    const r = indexedDB.open(name, OWNER_DB_VERSION)
    r.onupgradeneeded = () => createOwnerStores(r.result)
    r.onsuccess = () => resolve(r.result)
    r.onerror = () => reject(r.error)
  })
  try {
    const tx = db.transaction(STORE.fence, 'readwrite')
    const done = txDone(tx)
    const rec: FenceRecord = { owner_key: ownerKey, owner_incarnation: incarnation, fence_epoch: fenceEpoch, state: FENCE_OPEN }
    tx.objectStore(STORE.fence).put(rec, FENCE_KEY)
    await done
  } finally {
    db.close()
  }
}

export async function readStorageFence(ownerKey: string): Promise<FenceRecord | null> {
  const db = await openOwnerDb(ownerKey)
  if (!db) return null
  try {
    const tx = db.transaction(STORE.fence, 'readonly')
    const rec = await req(tx.objectStore(STORE.fence).get(FENCE_KEY))
    return (rec as FenceRecord | undefined) ?? null
  } catch {
    return null
  }
}

/**
 * Close the storage fence: a COMMITTED readwrite on `fence` setting state
 * closed. Every owner write includes `fence` in its scope, so when this
 * commits every earlier write has committed or aborted and every later one
 * reads `closed` (verified quiescence, C3 step 2). No database: nothing to fence.
 */
export async function closeStorageFence(ownerKey: string): Promise<void> {
  const db = await openOwnerDb(ownerKey)
  if (!db) return
  const tx = db.transaction(STORE.fence, 'readwrite')
  const done = txDone(tx)
  const store = tx.objectStore(STORE.fence)
  const g = store.get(FENCE_KEY)
  g.onsuccess = () => {
    const cur = g.result as FenceRecord | undefined
    if (cur) store.put({ ...cur, state: FENCE_CLOSED }, FENCE_KEY)
  }
  await done
}

export type FencedResult = 'ok' | 'refused'

/** Every owner write's body: issue requests on `tx` synchronously (or chained in their callbacks). */
export type FencedBody = (tx: IDBTransaction) => void

interface LocksLike {
  request: (name: string, opts: { mode: 'shared' | 'exclusive' }, cb: () => Promise<unknown>) => Promise<unknown>
}

function locks(): LocksLike | null {
  const l = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LocksLike }).locks : undefined
  return l && typeof l.request === 'function' ? l : null
}

/**
 * THE owner write (C3 mechanism 4). Resolves 'refused' when the gate is
 * closed (a transition, another owner, a moved fence epoch or incarnation,
 * a wiped database); rejects only with a storage error (quota, ...).
 * Runs inside the SHARED Web Lock cb-owner-write where the browser has it.
 */
export function fencedWrite(ctx: WriterCtx, stores: readonly OwnerStore[], body: FencedBody, guard?: () => boolean): Promise<FencedResult> {
  const run = async (): Promise<FencedResult> => {
    if (!mayWrite(ctx)) return 'refused'
    const db = await openOwnerDb(ctx.ownerKey)
    if (!db || !mayWrite(ctx)) return 'refused'
    const scope = [STORE.fence, ...stores.filter(s => s !== STORE.fence)]
    let tx: IDBTransaction
    try {
      tx = db.transaction(scope, 'readwrite')
    } catch {
      // The connection is closing (a delete is waiting on it).
      return 'refused'
    }
    return new Promise<FencedResult>((resolve, reject) => {
      let refused = false
      let bodyError: unknown = null
      let firstError: DOMException | null = null
      const g = tx.objectStore(STORE.fence).get(FENCE_KEY)
      g.onsuccess = () => {
        const f = g.result as FenceRecord | undefined
        if (!f || f.state !== FENCE_OPEN || f.owner_key !== ctx.ownerKey
          || f.owner_incarnation !== ctx.incarnation || f.fence_epoch !== ctx.fenceEpoch) {
          refused = true
          tx.abort()
          return
        }
        // A permission re-checked INSIDE the committing transaction (C2
        // rejected: no new edit or record, finding 2).
        if (guard && !guard()) {
          refused = true
          tx.abort()
          return
        }
        try {
          body(tx)
        } catch (e) {
          bodyError = e
          tx.abort()
        }
      }
      tx.onerror = (ev) => {
        const t = ev.target as IDBRequest | null
        if (!firstError && t && 'error' in t) firstError = t.error
      }
      tx.oncomplete = () => resolve('ok')
      tx.onabort = () => {
        if (refused) resolve('refused')
        else reject(bodyError ?? tx.error ?? firstError ?? new DOMException('Transaction aborted', 'AbortError'))
      }
    })
  }
  const l = locks()
  return l ? (l.request(LOCK_OWNER_WRITE, { mode: 'shared' }, run) as Promise<FencedResult>) : run()
}

/** Every value of one owner store (a read: never fenced, only ever for offline_owner, C5 rule 7). */
export async function ownerGetAll<T>(ownerKey: string, store: OwnerStore): Promise<T[]> {
  const db = await openOwnerDb(ownerKey)
  if (!db) return []
  try {
    const tx = db.transaction(store, 'readonly')
    return (await req(tx.objectStore(store).getAll())) as T[]
  } catch {
    return []
  }
}

export async function ownerGet<T>(ownerKey: string, store: OwnerStore, key: IDBValidKey): Promise<T | null> {
  const db = await openOwnerDb(ownerKey)
  if (!db) return null
  try {
    const tx = db.transaction(store, 'readonly')
    return ((await req(tx.objectStore(store).get(key))) as T | undefined) ?? null
  } catch {
    return null
  }
}

/** Whether the owner's database exists (tests and the C6 panel). */
export async function ownerDbExists(ownerKey: string): Promise<boolean> {
  const names = await listDatabaseNames()
  if (names) return names.includes(ownerDbName(ownerKey))
  return (await openOwnerDb(ownerKey)) !== null
}

export async function listDatabaseNames(): Promise<string[] | null> {
  if (!hasIndexedDb()) return []
  const f = (indexedDB as IDBFactory & { databases?: () => Promise<{ name?: string }[]> }).databases
  if (typeof f !== 'function') return null
  try {
    const list = await f.call(indexedDB)
    return list.map(d => d.name ?? '').filter(Boolean)
  } catch {
    return null
  }
}

const DELETE_WAIT_MS = 3000

/**
 * Delete ALL owner content (C3 step 7): every store but `fence` is cleared
 * and the fence stays closed, in one transaction (so content is gone even if
 * a frozen tab's open connection blocks the delete), then the database is
 * deleted. A delete still blocked after DELETE_WAIT_MS completes when the
 * other connection closes; the orphan sweep catches it otherwise.
 * Rejects when the content could not be cleared (the tombstone stays).
 */
export type WipeResult = 'deleted' | 'delete_pending'

export async function wipeOwnerDb(ownerKey: string): Promise<WipeResult> {
  if (!hasIndexedDb()) return 'deleted'
  const db = await openOwnerDb(ownerKey)
  if (db) {
    const tx = db.transaction([...OWNER_STORES], 'readwrite')
    const done = txDone(tx)
    for (const s of OWNER_STORES) {
      if (s === STORE.fence) continue
      tx.objectStore(s).clear()
    }
    const fs = tx.objectStore(STORE.fence)
    const g = fs.get(FENCE_KEY)
    g.onsuccess = () => {
      const cur = g.result as FenceRecord | undefined
      if (cur) fs.put({ ...cur, state: FENCE_CLOSED }, FENCE_KEY)
    }
    await done
  }
  closeOwnerConnection(ownerKey)
  const name = ownerDbName(ownerKey)
  const deleted = await deleteDatabase(name)
  if (deleted) return 'deleted'
  // Content is gone (cleared above); the NAME still exists until the other
  // connection closes. Tracked explicitly and retried (non-blocking note 6).
  await updateDeviceIndex(idx => {
    if (!idx.pending_db_deletes.includes(name)) idx.pending_db_deletes = [...idx.pending_db_deletes, name]
  })
  return 'delete_pending'
}

/** true: deleted; false: still blocked after DELETE_WAIT_MS (it completes when the other connection closes). */
function deleteDatabase(name: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    let settled = false
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn() } }
    const r = indexedDB.deleteDatabase(name)
    r.onsuccess = () => finish(() => resolve(true))
    r.onerror = () => finish(() => reject(r.error))
    r.onblocked = () => { setTimeout(() => finish(() => resolve(false)), DELETE_WAIT_MS) }
  })
}

/**
 * Owner images (W6) in Cache Storage for this owner, every incarnation; C3
 * deletes them. Verified: rejects when one is still there (the tombstone
 * stays and recovery retries).
 */
export async function deleteOwnerImageCache(ownerKey: string): Promise<void> {
  if (typeof caches === 'undefined') return
  for (const name of await caches.keys()) {
    if (isOwnerImageCacheOf(name, ownerKey)) await caches.delete(name)
  }
  if ((await caches.keys()).some(n => isOwnerImageCacheOf(n, ownerKey))) throw new Error('owner images not deleted')
}

/**
 * The orphan sweep (C3 inv. 12), at every page load and after every
 * transition: any per-owner database or owner image cache whose owner is not
 * `offline_owner` and has no pending tombstone is deleted (a tombstoned one
 * is finished by recovery instead).
 */
export async function sweepOrphans(idx: DeviceIndex): Promise<string[]> {
  const keep = new Set<string>()
  if (idx.offline_owner) keep.add(idx.offline_owner)
  for (const t of idx.pending_wipes) keep.add(t.owner_key)
  const removed: string[] = []
  const listed = await listDatabaseNames()
  // Tracked blocked deletes are retried even where databases() is missing.
  const names = new Set([...(listed ?? []), ...idx.pending_db_deletes])
  const done: string[] = []
  for (const name of names) {
    const owner = ownerKeyOfDbName(name)
    if (!owner || keep.has(owner)) continue
    closeOwnerConnection(owner)
    try {
      if (await deleteDatabase(name)) {
        removed.push(name)
        done.push(name)
      }
    } catch {
      // Retried at the next load.
    }
  }
  if (done.length && idx.pending_db_deletes.some(n => done.includes(n))) {
    await updateDeviceIndex(i => { i.pending_db_deletes = i.pending_db_deletes.filter(n => !done.includes(n)) }).catch(() => undefined)
  }
  if (typeof caches !== 'undefined') {
    try {
      // offline_owner keeps ONLY its current incarnation's cache (an older
      // one is a stale writer's leftover); a tombstoned owner keeps all of its
      // caches until recovery wipes them.
      const keepCache = (name: string) =>
        (idx.offline_owner !== null && name === ownerImageCacheName(idx.offline_owner, incarnationOf(idx, idx.offline_owner)))
        || idx.pending_wipes.some(t => isOwnerImageCacheOf(name, t.owner_key))
      for (const name of await caches.keys()) {
        if (name.startsWith(OWNER_IMAGE_CACHE_PREFIX) && !keepCache(name)) {
          await caches.delete(name)
          removed.push(name)
        }
      }
    } catch {
      // Cache Storage unavailable: nothing to sweep.
    }
  }
  return removed
}

/** Test seam: forget cached connections (fake-indexeddb is reset per test). */
export function __resetOfflineStoreForTests(): void {
  for (const p of ownerConns.values()) void p.then(db => db?.close(), () => {})
  ownerConns.clear()
  void deviceDb?.then(db => db.close(), () => {})
  deviceDb = null
}

export { isAccountOwner }
